import { useState, useEffect, useMemo, useCallback } from 'react';
import { uploadImage, uploadVideo } from '../services/MediaUploadService';
import { saveExercise, checkExerciseNameExists } from '../services/ExcersiceService';
import { getVideoType } from '../utils/videoUtils';
import { generateThumbnailFilename } from '../utils/videoThumbnailUtils';
import type { AdminExercise, CompanyPermissions } from '../api/generated';

// The exercise object the form is initialized from. It is the server DTO plus the
// runtime `removeCover` flag the save payload may add (not part of the read DTO).
export type ExerciseInput = AdminExercise;

// Editable form fields (subset of AdminExercise the form controls directly).
export interface ExerciseFormValues {
    id: number | null;
    name: string;
    nameHeb: string;
    targetMuscle: AdminExercise['targetMuscle'] | null;
    resistanceType: AdminExercise['resistanceType'] | null;
    description: string;
    descriptionHeb: string;
    videoUrl: string;
    systemResource: boolean;
    companyPermissions: Array<CompanyPermissions> | null;
    defaultMinReps: number | null;
    defaultMaxReps: number | null;
    catalyst: boolean;
}

export type UploadPhase = 'idle' | 'uploading' | 'compressing' | 'complete' | 'error';

export interface UploadState {
    phase: UploadPhase;
    uploadProgress: number;
    compressionProgress: number;
    error: string | null;
}

export interface UseExerciseFormOptions {
    onSaveSuccess?: (savedExercise: AdminExercise | undefined) => void;
    onSaveError?: (error: unknown) => void;
}

export interface SaveResult {
    success: boolean;
    data?: AdminExercise;
    error?: unknown;
}

/**
 * Custom hook for managing exercise form state, validation, and uploads.
 * Used by both ExerciseDialog and ExercisePage for unified form handling.
 */
export const useExerciseForm = (
    initialExercise: ExerciseInput | null | undefined,
    options: UseExerciseFormOptions = {},
) => {
    const {
        onSaveSuccess,
        onSaveError,
    } = options;

    // Form values
    const [formValues, setFormValues] = useState<ExerciseFormValues>({
        id: null,
        name: '',
        nameHeb: '',
        targetMuscle: null,
        resistanceType: null,
        description: '',
        descriptionHeb: '',
        videoUrl: '',
        systemResource: false,
        companyPermissions: null,
        defaultMinReps: null,
        defaultMaxReps: null,
        catalyst: false,
    });

    // Original values for change detection
    const [originalValues, setOriginalValues] = useState<ExerciseFormValues | null>(null);

    // Upload states
    const [imageUploadState, setImageUploadState] = useState<UploadState>({
        phase: 'idle',
        uploadProgress: 0,
        compressionProgress: 0,
        error: null
    });
    const [coverUploadState, setCoverUploadState] = useState<UploadState>({
        phase: 'idle',
        uploadProgress: 0,
        compressionProgress: 0,
        error: null
    });
    const [videoUploadState, setVideoUploadState] = useState<UploadState>({
        phase: 'idle',
        uploadProgress: 0,
        compressionProgress: 0,
        error: null
    });

    // CDN URLs for uploaded files
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
    const [uploadedCoverUrl, setUploadedCoverUrl] = useState<string | null>(null);
    const [uploadedVideoUrl, setUploadedVideoUrl] = useState<string | null>(null);

    // Image previews (local blob URLs or CDN URLs)
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [coverPreview, setCoverPreview] = useState<string | null>(null);

    // Video states
    const [isVideoUploadMode, setIsVideoUploadMode] = useState(false);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [videoFileName, setVideoFileName] = useState('');
    const [existingVideoUrl, setExistingVideoUrl] = useState<string | null>(null);
    const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
    const [pendingCoverFile, setPendingCoverFile] = useState<File | null>(null);

    // Form state
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string | undefined>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    // Name existence validation
    const [nameExistsError, setNameExistsError] = useState<string | null>(null);
    const [nameHebExistsError, setNameHebExistsError] = useState<string | null>(null);
    const [isCheckingName, setIsCheckingName] = useState(false);
    const [isCheckingNameHeb, setIsCheckingNameHeb] = useState(false);

    // Initialize form from exercise data
    useEffect(() => {
        if (initialExercise) {
            const values = {
                id: initialExercise.id || null,
                name: initialExercise.name || '',
                nameHeb: initialExercise.nameHeb || '',
                targetMuscle: initialExercise.targetMuscle || null,
                resistanceType: initialExercise.resistanceType || null,
                description: initialExercise.description || '',
                descriptionHeb: initialExercise.descriptionHeb || '',
                videoUrl: initialExercise.videoUrl || '',
                systemResource: initialExercise.systemResource || false,
                companyPermissions: initialExercise.companyPermissions || null,
                defaultMinReps: initialExercise.defaultMinReps || null,
                defaultMaxReps: initialExercise.defaultMaxReps || null,
                catalyst: initialExercise.catalyst || false,
            };
            setFormValues(values);
            setOriginalValues(JSON.parse(JSON.stringify(values)));

            // Initialize image previews
            setImagePreview(initialExercise.imageUrl || null);
            setCoverPreview(initialExercise.coverUrl || null);

            // Initialize video mode
            const videoType = getVideoType(initialExercise.videoUrl);
            const isS3Video = videoType === 's3';

            if (initialExercise.videoFileName || isS3Video) {
                setIsVideoUploadMode(true);
                const displayFileName = initialExercise.videoFileName ||
                    (isS3Video ? initialExercise.videoUrl?.split('/').pop() || '' : '');
                setVideoFileName(displayFileName);
                if (isS3Video) {
                    setExistingVideoUrl(initialExercise.videoUrl ?? null);
                }
            } else {
                setIsVideoUploadMode(false);
                setVideoFileName('');
                setExistingVideoUrl(null);
            }

            // Reset upload states
            setVideoFile(null);
            setUploadedImageUrl(null);
            setUploadedCoverUrl(null);
            setUploadedVideoUrl(null);
            setPendingCoverFile(null);
        } else {
            // New exercise - reset everything
            setFormValues({
                id: null,
                name: '',
                nameHeb: '',
                targetMuscle: null,
                resistanceType: null,
                description: '',
                descriptionHeb: '',
                videoUrl: '',
                systemResource: false,
                companyPermissions: null,
                defaultMinReps: null,
                defaultMaxReps: null,
                catalyst: false,
            });
            setOriginalValues(null);
            setImagePreview(null);
            setCoverPreview(null);
            setIsVideoUploadMode(false);
            setVideoFileName('');
            setExistingVideoUrl(null);
            setVideoFile(null);
            setUploadedImageUrl(null);
            setUploadedCoverUrl(null);
            setUploadedVideoUrl(null);
            setPendingCoverFile(null);
        }

        // Reset validation states
        setErrors({});
        setTouched({});
        setNameExistsError(null);
        setNameHebExistsError(null);
    }, [initialExercise?.id]);

    // Manage video preview URL
    useEffect(() => {
        if (uploadedVideoUrl) {
            setVideoPreviewUrl(uploadedVideoUrl);
            return;
        }
        if (videoFile) {
            const blobUrl = URL.createObjectURL(videoFile);
            setVideoPreviewUrl(blobUrl);
            return () => URL.revokeObjectURL(blobUrl);
        }
        if (existingVideoUrl) {
            setVideoPreviewUrl(existingVideoUrl);
            return;
        }
        setVideoPreviewUrl(null);
    }, [videoFile, uploadedVideoUrl, existingVideoUrl]);

    // Upload pending cover file with debounce
    useEffect(() => {
        if (!pendingCoverFile) return;

        const timeoutId = setTimeout(async () => {
            setCoverUploadState({
                phase: 'uploading',
                uploadProgress: 0,
                compressionProgress: 0,
                error: null
            });
            try {
                const cdnUrl = await uploadImage(
                    pendingCoverFile,
                    (uploadProgress: number) => setCoverUploadState(prev => ({
                        ...prev,
                        phase: 'uploading',
                        uploadProgress
                    })),
                    (compressionProgress: number) => setCoverUploadState(prev => ({
                        ...prev,
                        phase: 'compressing',
                        uploadProgress: 100,
                        compressionProgress
                    }))
                );
                setUploadedCoverUrl(cdnUrl);
                setUploadedImageUrl(cdnUrl);
                setCoverUploadState({
                    phase: 'complete',
                    uploadProgress: 100,
                    compressionProgress: 100,
                    error: null
                });
                setTimeout(() => setCoverUploadState({
                    phase: 'idle',
                    uploadProgress: 0,
                    compressionProgress: 0,
                    error: null
                }), 2000);
            } catch (error) {
                console.error('Thumbnail upload failed:', error);
                setCoverUploadState({
                    phase: 'error',
                    uploadProgress: 0,
                    compressionProgress: 0,
                    error: error instanceof Error ? error.message : String(error)
                });
            }
            setPendingCoverFile(null);
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [pendingCoverFile]);

    // Check if uploading
    const isUploading = useMemo(() => {
        return imageUploadState.phase === 'uploading' || imageUploadState.phase === 'compressing' ||
            coverUploadState.phase === 'uploading' || coverUploadState.phase === 'compressing' ||
            videoUploadState.phase === 'uploading' || videoUploadState.phase === 'compressing';
    }, [imageUploadState.phase, coverUploadState.phase, videoUploadState.phase]);

    // Check if form has changes
    const hasChanges = useMemo(() => {
        if (!originalValues) return true; // New exercise always has "changes"

        const formString = JSON.stringify(formValues);
        const originalString = JSON.stringify(originalValues);
        const dataChanged = formString !== originalString;

        const hasNewUploads = uploadedImageUrl || uploadedCoverUrl || uploadedVideoUrl;
        const hasPendingCover = pendingCoverFile !== null;

        return dataChanged || hasNewUploads || hasPendingCover;
    }, [formValues, originalValues, uploadedImageUrl, uploadedCoverUrl, uploadedVideoUrl, pendingCoverFile]);

    // Validate form
    const validate = useCallback(() => {
        const newErrors: Record<string, string> = {};

        // `name` (the legacy English column) is NOT validated and NOT edited by the user any more.
        // English moved into the translation panel, which writes `entity_translation` at locale
        // `en`; the column is carried through unchanged on save (see handleSave) and is dropped by
        // the Phase-3 migration. Requiring it here would block every save the moment the English
        // TextField disappeared.
        if (!formValues.nameHeb || formValues.nameHeb.trim().length < 2) {
            newErrors.nameHeb = 'שם התרגיל בעברית חייב להכיל לפחות 2 תווים';
        }

        if (!formValues.targetMuscle) {
            newErrors.targetMuscle = 'שריר יעד הוא שדה חובה';
        }

        if (!formValues.resistanceType) {
            newErrors.resistanceType = 'סוג התנגדות הוא שדה חובה';
        }

        if (formValues.description && formValues.description.length > 1000) {
            newErrors.description = 'התיאור לא יכול להכיל יותר מ-1000 תווים';
        }

        if (formValues.descriptionHeb && formValues.descriptionHeb.length > 1000) {
            newErrors.descriptionHeb = 'התיאור לא יכול להכיל יותר מ-1000 תווים';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formValues]);

    const isValid = useMemo(() => {
        // Mirrors validate(): the canonical (Hebrew) name is the required one. `nameExistsError` is
        // still consulted so a stale English-uniqueness verdict can't be ignored, but nothing sets
        // it any more — the English input is gone (see validate()).
        return !nameExistsError &&
            !nameHebExistsError &&
            Object.keys(errors).length === 0 &&
            formValues.nameHeb?.trim().length >= 2 &&
            formValues.targetMuscle &&
            formValues.resistanceType;
    }, [errors, nameExistsError, nameHebExistsError, formValues]);

    // Set field value
    const setFieldValue = useCallback(<K extends keyof ExerciseFormValues>(
        field: K,
        value: ExerciseFormValues[K],
    ) => {
        setFormValues(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    }, [errors]);

    // Set field touched
    const setFieldTouched = useCallback((field: string) => {
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));
    }, []);

    // Check name existence.
    //
    // NOTE: `checkNameExists` (the ENGLISH-name check) is currently unwired — the English TextField
    // became a translation row, so no UI calls it and `nameExistsError` stays null. It is kept
    // rather than deleted because Phase 3 renames `name_heb` -> `name`, at which point this
    // endpoint checks the CANONICAL name and the plumbing is wanted again. `checkNameHebExists` is
    // the live one.
    const checkNameExists = useCallback(async (name: string) => {
        if (!name || name.trim() === '') {
            setNameExistsError(null);
            return;
        }

        setIsCheckingName(true);
        try {
            // ExcersiceService is untyped JS: its `excludeId` param is inferred `null | undefined`
            // though the endpoint accepts a numeric id at runtime, so the id is cast to that param
            // type. The service returns { data: <flat check-name payload> }.
            const response = await checkExerciseNameExists(
                name,
                null,
                (formValues.id ?? undefined) as null | undefined,
            );
            // ExcersiceService.checkExerciseNameExists always returns { data: <flat check payload> },
            // so response.data is the shape — no fallback branch is reachable.
            const result: Record<string, boolean> | undefined = response?.data;
            if (result?.nameExists) {
                setNameExistsError('תרגיל עם שם זה באנגלית כבר קיים במערכת');
            } else {
                setNameExistsError(null);
            }
        } catch (error) {
            console.error('Error checking name:', error);
            setNameExistsError(null);
        } finally {
            setIsCheckingName(false);
        }
    }, [formValues.id]);

    const checkNameHebExists = useCallback(async (nameHeb: string) => {
        if (!nameHeb || nameHeb.trim() === '') {
            setNameHebExistsError(null);
            return;
        }

        setIsCheckingNameHeb(true);
        try {
            // See checkNameExists — the same untyped-JS caveat applies to the Hebrew check.
            const response = await checkExerciseNameExists(
                null,
                nameHeb,
                (formValues.id ?? undefined) as null | undefined,
            );
            // See checkNameExists — the service always wraps as { data }, so no fallback is needed.
            const result: Record<string, boolean> | undefined = response?.data;
            if (result?.nameHebExists) {
                setNameHebExistsError('תרגיל עם שם זה בעברית כבר קיים במערכת');
            } else {
                setNameHebExistsError(null);
            }
        } catch (error) {
            console.error('Error checking Hebrew name:', error);
            setNameHebExistsError(null);
        } finally {
            setIsCheckingNameHeb(false);
        }
    }, [formValues.id]);

    // Handle image upload
    const handleImageUpload = useCallback(async (file: File, type: 'image' | 'cover') => {
        if (!file) return;

        const setUploadState = type === 'image' ? setImageUploadState : setCoverUploadState;
        const previewUrl = URL.createObjectURL(file);

        // Show local preview immediately
        if (type === 'image') {
            setImagePreview(previewUrl);
        } else {
            setCoverPreview(previewUrl);
            setImagePreview(previewUrl);
        }

        // Start upload
        setUploadState({
            phase: 'uploading',
            uploadProgress: 0,
            compressionProgress: 0,
            error: null
        });

        try {
            const cdnUrl = await uploadImage(
                file,
                (uploadProgress: number) => setUploadState(prev => ({
                    ...prev,
                    phase: 'uploading',
                    uploadProgress
                })),
                (compressionProgress: number) => setUploadState(prev => ({
                    ...prev,
                    phase: 'compressing',
                    uploadProgress: 100,
                    compressionProgress
                }))
            );

            // Update CDN URLs
            if (type === 'image') {
                setUploadedImageUrl(cdnUrl);
                setImagePreview(cdnUrl);
            } else {
                setUploadedCoverUrl(cdnUrl);
                setUploadedImageUrl(cdnUrl);
                setCoverPreview(cdnUrl);
                setImagePreview(cdnUrl);
            }

            setUploadState({
                phase: 'complete',
                uploadProgress: 100,
                compressionProgress: 100,
                error: null
            });

            setTimeout(() => setUploadState({
                phase: 'idle',
                uploadProgress: 0,
                compressionProgress: 0,
                error: null
            }), 2000);

            return cdnUrl;
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadState({
                phase: 'error',
                uploadProgress: 0,
                compressionProgress: 0,
                error: error instanceof Error ? error.message : String(error)
            });
            throw error;
        }
    }, []);

    // Handle cover extracted from video
    const handleCoverExtracted = useCallback((
        blob: Blob | null,
        previewUrl: string | null,
        time?: number,
    ) => {
        if (blob) {
            const thumbnailFileName = generateThumbnailFilename(videoFileName || 'video');
            const file = new File([blob], thumbnailFileName, { type: 'image/jpeg' });
            setCoverPreview(previewUrl);
            setImagePreview(previewUrl);

            // Cancel previous upload and set new pending file
            setUploadedCoverUrl(null);
            setUploadedImageUrl(null);
            setPendingCoverFile(file);
        } else {
            setCoverPreview(null);
            setPendingCoverFile(null);
        }
    }, [videoFileName]);

    // Handle video file change
    const handleVideoFileChange = useCallback(async (file: File) => {
        if (!file) return;

        setVideoFile(file);
        setVideoFileName(file.name);
        setExistingVideoUrl(null);
        setCoverPreview(null);
        setPendingCoverFile(null);

        // Start video upload
        setVideoUploadState({
            phase: 'uploading',
            uploadProgress: 0,
            compressionProgress: 0,
            error: null
        });

        try {
            const cdnUrl = await uploadVideo(
                file,
                (uploadProgress: number) => setVideoUploadState(prev => ({
                    ...prev,
                    phase: 'uploading',
                    uploadProgress
                })),
                (compressionProgress: number) => setVideoUploadState(prev => ({
                    ...prev,
                    phase: 'compressing',
                    uploadProgress: 100,
                    compressionProgress
                }))
            );

            setUploadedVideoUrl(cdnUrl);
            setVideoUploadState({
                phase: 'complete',
                uploadProgress: 100,
                compressionProgress: 100,
                error: null
            });

            setTimeout(() => setVideoUploadState({
                phase: 'idle',
                uploadProgress: 0,
                compressionProgress: 0,
                error: null
            }), 2000);

            // Extract first frame as thumbnail
            extractFirstFrame(file);

            return cdnUrl;
        } catch (error) {
            console.error('Video upload failed:', error);
            setVideoUploadState({
                phase: 'error',
                uploadProgress: 0,
                compressionProgress: 0,
                error: error instanceof Error ? error.message : String(error)
            });
            throw error;
        }
    }, []);

    // Extract first frame from video
    const extractFirstFrame = useCallback((file: File) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = URL.createObjectURL(file);

        video.onloadeddata = () => {
            video.currentTime = 0;
        };

        video.onseeked = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;

            const videoWidth = video.videoWidth;
            const videoHeight = video.videoHeight;
            const maxWidth = 1280;
            const maxHeight = 720;

            let outputWidth = videoWidth;
            let outputHeight = videoHeight;

            if (outputWidth > maxWidth) {
                outputHeight = (maxWidth / outputWidth) * outputHeight;
                outputWidth = maxWidth;
            }
            if (outputHeight > maxHeight) {
                outputWidth = (maxHeight / outputHeight) * outputWidth;
                outputHeight = maxHeight;
            }

            canvas.width = outputWidth;
            canvas.height = outputHeight;
            ctx.drawImage(video, 0, 0, outputWidth, outputHeight);

            canvas.toBlob((blob) => {
                if (blob) {
                    const thumbnailFileName = generateThumbnailFilename(file.name);
                    const thumbnailFile = new File([blob], thumbnailFileName, { type: 'image/jpeg' });
                    const previewUrl = URL.createObjectURL(blob);

                    setCoverPreview(previewUrl);
                    setImagePreview(previewUrl);
                    setPendingCoverFile(thumbnailFile);
                }
                URL.revokeObjectURL(video.src);
            }, 'image/jpeg', 0.95);
        };
    }, []);

    // Handle video URL change
    const handleVideoUrlChange = useCallback((url: string) => {
        setFieldValue('videoUrl', url);
    }, [setFieldValue]);

    // Handle remove video
    const handleRemoveVideo = useCallback(() => {
        setVideoFile(null);
        setVideoFileName('');
        setExistingVideoUrl(null);
        setCoverPreview(null);
        setUploadedVideoUrl(null);
        setUploadedCoverUrl(null);
        setPendingCoverFile(null);
        setVideoUploadState({
            phase: 'idle',
            uploadProgress: 0,
            compressionProgress: 0,
            error: null
        });
        setCoverUploadState({
            phase: 'idle',
            uploadProgress: 0,
            compressionProgress: 0,
            error: null
        });
    }, []);

    // Handle save
    const handleSave = useCallback(async (): Promise<SaveResult> => {
        if (!validate()) {
            return { success: false, error: 'Validation failed' };
        }

        if (isUploading) {
            return { success: false, error: 'Uploads in progress' };
        }

        setIsSaving(true);

        try {
            const exerciseData: Record<string, unknown> = {
                id: formValues.id,
                // `exercises.name` is the legacy English column and is `nullable = false`, but the
                // user no longer types into it — English is a translation row now. So:
                //   - EDITING: pass the loaded value straight through. Rewriting it from Hebrew
                //     would corrupt the English an installed mobile binary still reads (it sends no
                //     Accept-Language, so it gets the raw column, not a resolved translation).
                //   - CREATING: there is nothing to pass through and the column cannot be null, so
                //     it takes the Hebrew name. That is not pollution — it is exactly the state
                //     Phase 3 converges on (name_heb is renamed to name), and the backfill's
                //     "contains a Hebrew codepoint" guard already treats such a value as
                //     NO English translation rather than as a real one.
                // Same pattern the measurement-definition form has always used.
                name: formValues.name?.trim() || formValues.nameHeb?.trim(),
                nameHeb: formValues.nameHeb,
                targetMuscle: formValues.targetMuscle,
                resistanceType: formValues.resistanceType,
                description: formValues.description,
                descriptionHeb: formValues.descriptionHeb,
                systemResource: formValues.systemResource,
                companyPermissions: formValues.companyPermissions,
                defaultMinReps: formValues.defaultMinReps,
                defaultMaxReps: formValues.defaultMaxReps,
                catalyst: formValues.catalyst,
                // Image URLs
                imageUrl: uploadedImageUrl || initialExercise?.imageUrl || null,
                coverUrl: uploadedCoverUrl || initialExercise?.coverUrl || null,
                // Video handling
                videoUrl: isVideoUploadMode
                    ? (uploadedVideoUrl || existingVideoUrl || null)
                    : (formValues.videoUrl || null),
                videoFileName: isVideoUploadMode ? videoFileName : null,
            };

            // Check if cover was removed
            if (initialExercise?.coverUrl && !coverPreview && !uploadedCoverUrl) {
                exerciseData.removeCover = true;
            }

            const response = await saveExercise(exerciseData);
            const savedExercise = response.data;

            // Reset upload states
            setUploadedImageUrl(null);
            setUploadedCoverUrl(null);
            setUploadedVideoUrl(null);
            setPendingCoverFile(null);

            if (onSaveSuccess) {
                onSaveSuccess(savedExercise);
            }

            return { success: true, data: savedExercise };
        } catch (error) {
            console.error('Save failed:', error);
            if (onSaveError) {
                onSaveError(error);
            }
            return { success: false, error };
        } finally {
            setIsSaving(false);
        }
    }, [
        validate,
        isUploading,
        formValues,
        uploadedImageUrl,
        uploadedCoverUrl,
        uploadedVideoUrl,
        isVideoUploadMode,
        existingVideoUrl,
        videoFileName,
        initialExercise,
        coverPreview,
        onSaveSuccess,
        onSaveError
    ]);

    // Reset form
    const resetForm = useCallback(() => {
        if (initialExercise) {
            // NOTE: `catalyst` is intentionally left off here to preserve the exact
            // pre-migration runtime (resetForm did not carry catalyst). Cast keeps that
            // behavior while satisfying the ExerciseFormValues shape.
            const values = {
                id: initialExercise.id || null,
                name: initialExercise.name || '',
                nameHeb: initialExercise.nameHeb || '',
                targetMuscle: initialExercise.targetMuscle || null,
                resistanceType: initialExercise.resistanceType || null,
                description: initialExercise.description || '',
                descriptionHeb: initialExercise.descriptionHeb || '',
                videoUrl: initialExercise.videoUrl || '',
                systemResource: initialExercise.systemResource || false,
                companyPermissions: initialExercise.companyPermissions || null,
                defaultMinReps: initialExercise.defaultMinReps || null,
                defaultMaxReps: initialExercise.defaultMaxReps || null,
            } as ExerciseFormValues;
            setFormValues(values);
            setImagePreview(initialExercise.imageUrl || null);
            setCoverPreview(initialExercise.coverUrl || null);
        } else {
            setFormValues({
                id: null,
                name: '',
                nameHeb: '',
                targetMuscle: null,
                resistanceType: null,
                description: '',
                descriptionHeb: '',
                videoUrl: '',
                systemResource: false,
                companyPermissions: null,
                defaultMinReps: null,
                defaultMaxReps: null,
                catalyst: false,
            });
            setImagePreview(null);
            setCoverPreview(null);
        }

        // Reset video state based on initial exercise
        const videoType = getVideoType(initialExercise?.videoUrl);
        const isS3Video = videoType === 's3';
        if (initialExercise?.videoFileName || isS3Video) {
            setIsVideoUploadMode(true);
            setVideoFileName(initialExercise?.videoFileName || '');
            setExistingVideoUrl(isS3Video ? (initialExercise?.videoUrl ?? null) : null);
        } else {
            setIsVideoUploadMode(false);
            setVideoFileName('');
            setExistingVideoUrl(null);
        }

        // Reset upload states
        setVideoFile(null);
        setUploadedImageUrl(null);
        setUploadedCoverUrl(null);
        setUploadedVideoUrl(null);
        setPendingCoverFile(null);
        setErrors({});
        setTouched({});
        setNameExistsError(null);
        setNameHebExistsError(null);

        // Reset upload progress states
        setImageUploadState({ phase: 'idle', uploadProgress: 0, compressionProgress: 0, error: null });
        setCoverUploadState({ phase: 'idle', uploadProgress: 0, compressionProgress: 0, error: null });
        setVideoUploadState({ phase: 'idle', uploadProgress: 0, compressionProgress: 0, error: null });
    }, [initialExercise]);

    return {
        // Form values
        formValues,
        setFieldValue,
        setFieldTouched,

        // Upload states
        uploadStates: {
            image: imageUploadState,
            cover: coverUploadState,
            video: videoUploadState
        },
        isUploading,

        // Previews
        imagePreview,
        coverPreview,

        // Video state
        isVideoUploadMode,
        setIsVideoUploadMode,
        videoFile,
        videoFileName,
        videoPreviewUrl,
        existingVideoUrl,

        // Handlers
        handleImageUpload,
        handleCoverExtracted,
        handleVideoFileChange,
        handleVideoUrlChange,
        handleRemoveVideo,

        // Validation
        errors,
        touched,
        isValid,
        hasChanges,
        validate,
        nameExistsError,
        nameHebExistsError,
        isCheckingName,
        isCheckingNameHeb,
        checkNameExists,
        checkNameHebExists,

        // Submit
        handleSave,
        isSaving,

        // Reset
        resetForm,
    };
};

export default useExerciseForm;
